import { useMemo, useState } from 'react';
import { Link2, Search } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Fab } from '../../../components/Fab';
import { CreatorFilterChips } from '../components/CreatorFilterChips';
import { RecipeCard } from '../components/RecipeCard';
import { RecipeListRow } from '../components/RecipeListRow';
import { ViewToggle } from '../components/ViewToggle';
import { useRecipes, useRecipesDoc } from '../hooks';
import { useHouseholdRoster, type RosterMember } from '../useHouseholdRoster';
import { filterRecipes } from '../utils/search';
import {
  readStoredViewPreference,
  writeStoredViewPreference,
  type RecipesViewPreference,
} from '../utils/viewPreference';

export function RecipeBoxPage(): JSX.Element {
  const navigate = useNavigate();
  const doc = useRecipesDoc();
  const { recipes } = useRecipes(doc);
  const { getMember } = useHouseholdRoster();
  const [query, setQuery] = useState('');
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [activeCreatorId, setActiveCreatorId] = useState<string | null>(null);
  const [view, setView] = useState<RecipesViewPreference>(() =>
    readStoredViewPreference(window.localStorage),
  );

  function handleViewChange(next: RecipesViewPreference): void {
    setView(next);
    writeStoredViewPreference(window.localStorage, next);
  }

  const allTags = useMemo(() => {
    const tags = new Set<string>();
    recipes.forEach((r) => r.tags.forEach((t) => tags.add(t)));
    return [...tags].sort((a, b) => a.localeCompare(b));
  }, [recipes]);

  // One chip per member who has actually added a recipe (not the full roster) — ordered by
  // when they first show up in the (already newest-first) recipe list, so the most recently
  // active contributor's chip tends to sit first.
  const creators = useMemo(() => {
    const seen = new Set<string>();
    const list: RosterMember[] = [];
    for (const r of recipes) {
      if (seen.has(r.createdBy)) continue;
      seen.add(r.createdBy);
      list.push(getMember(r.createdBy));
    }
    return list;
  }, [recipes, getMember]);

  const filtered = useMemo(
    () => filterRecipes(recipes, query, activeTag, activeCreatorId),
    [recipes, query, activeTag, activeCreatorId],
  );

  return (
    <div className="flex min-h-full flex-col gap-4 pb-24">
      <header className="flex items-center justify-between gap-3 px-4 pt-4">
        <h1 className="font-display text-2xl leading-none tracking-tight text-fg">Recipes</h1>
        <ViewToggle view={view} onChange={handleViewChange} />
      </header>

      <div className="px-4">
        <label className="flex h-11 items-center gap-2 rounded-md border-2 border-line-strong bg-surface px-3">
          <Search className="h-4 w-4 shrink-0 text-fg-2" aria-hidden="true" />
          <input
            data-testid="recipe-search-input"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search recipes, ingredients, tags"
            className="w-full bg-transparent text-[15px] text-fg placeholder:text-fg-3 focus:outline-none"
          />
        </label>
      </div>

      {allTags.length > 0 && (
        <div className="flex gap-2 overflow-x-auto px-4" data-testid="recipe-tag-filter">
          <button
            type="button"
            onClick={() => setActiveTag(null)}
            className={`h-9 shrink-0 rounded-full px-3 text-[13px] font-semibold ease-app ${
              activeTag === null ? 'bg-inverse text-inverse-fg' : 'border border-line text-fg-2'
            }`}
          >
            All {recipes.length}
          </button>
          {allTags.map((tag) => (
            <button
              key={tag}
              type="button"
              data-testid="recipe-tag-chip"
              onClick={() => setActiveTag(tag === activeTag ? null : tag)}
              className={`h-9 shrink-0 rounded-full px-3 text-[13px] font-semibold ease-app ${
                activeTag === tag ? 'bg-inverse text-inverse-fg' : 'border border-line text-fg-2'
              }`}
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      {creators.length > 1 && (
        <CreatorFilterChips
          creators={creators}
          activeCreatorId={activeCreatorId}
          onChange={setActiveCreatorId}
        />
      )}

      {recipes.length === 0 ? (
        <EmptyBox />
      ) : filtered.length === 0 ? (
        <NoSearchHits query={query} />
      ) : view === 'list' ? (
        <div data-testid="recipe-list" className="flex flex-col gap-2 px-4">
          {filtered.map((recipe) => (
            <RecipeListRow key={recipe.id} recipe={recipe} />
          ))}
        </div>
      ) : (
        <div
          data-testid="recipe-grid"
          className="grid grid-cols-2 gap-3 px-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
        >
          {filtered.map((recipe) => (
            <RecipeCard key={recipe.id} recipe={recipe} />
          ))}
        </div>
      )}

      <Fab label="Add recipe" testId="recipe-add-button" onClick={() => navigate('/recipes/new')} />
    </div>
  );
}

function EmptyBox(): JSX.Element {
  return (
    <div className="flex flex-col gap-3 px-4 py-6" data-testid="recipe-empty-box">
      <div className="w-12 border-t-2 border-fg" />
      <p className="text-xl font-bold tracking-tight text-fg">The recipe box is empty</p>
      <p className="text-[15px] leading-relaxed text-fg-2">
        Paste a link and we&apos;ll pull out the ingredients. Or start with one you know by heart.
      </p>
      <Link
        to="/recipes/new"
        className="inline-flex h-12 w-fit items-center gap-2 rounded-md bg-inverse px-5 text-[15px] font-semibold text-inverse-fg"
      >
        <Link2 className="h-4 w-4" aria-hidden="true" />
        Paste a link
      </Link>
    </div>
  );
}

function NoSearchHits({ query }: { query: string }): JSX.Element {
  return (
    <div className="flex flex-col gap-2 px-4 py-6" data-testid="recipe-no-search-hits">
      <div className="w-12 border-t-2 border-fg" />
      <p className="text-[17px] font-bold text-fg">No &ldquo;{query}&rdquo; in here</p>
      <p className="text-sm text-fg-2">Searched names, ingredients, and tags.</p>
      <Link to="/recipes/new" className="text-[13px] font-semibold text-accent">
        Add a &ldquo;{query}&rdquo; recipe →
      </Link>
    </div>
  );
}
